import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, Pencil, UserX, Undo2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ManualPunchModal from '../components/ManualPunchModal';
import apiClient from '../api/axios';

function pad(n) {
  return String(n).padStart(2, '0');
}

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatTime(isoInstant) {
  if (!isoInstant) return null;
  return new Date(isoInstant).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatScheduled(start, end) {
  if (!start || !end) return null;
  const fmt = t => {
    const [h, m] = t.split(':');
    return new Date(2000, 0, 1, Number(h), Number(m)).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatWorked(minutes) {
  if (minutes == null) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function StatusPill({ status, t }) {
  const styles = {
    ON_TIME: 'bg-green-50 text-green-700 border-green-200',
    LATE: 'bg-amber-50 text-amber-700 border-amber-200',
    NOT_IN_YET: 'bg-gray-100 text-gray-500 border-gray-200',
    ABSENT: 'bg-red-50 text-red-700 border-red-200',
    NO_PUNCH: 'bg-gray-100 text-gray-500 border-gray-200',
    NOT_SCHEDULED: 'bg-gray-50 text-gray-400 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.NOT_SCHEDULED}`}>
      {t(`status.${status}`)}
    </span>
  );
}

function Attendance() {
  const { t } = useTranslation('attendance');

  const [date, setDate] = useState(todayYMD());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [rowActionId, setRowActionId] = useState(null);
  const [rowActionError, setRowActionError] = useState(null);

  const fetchDay = useCallback(async (forDate) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/attendance', { params: { date: forDate } });
      setRows(res.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleSaved = (updatedRow) => {
    setRows(prev => prev.map(r => (r.employeeId === updatedRow.employeeId ? updatedRow : r)));
    setEditingRow(null);
  };

  // Story 26 Item 4 — inline row actions for Story 24's mark-absent/clear-absent, so a daily
  // absence sweep doesn't require opening the Edit modal for every no-punch row. Same two
  // endpoints, same guard conditions (mirrors ManualPunchModal's canMarkAbsent/canClearAbsent)
  // as the modal path, so behaviour can't drift between the two entry points.
  const handleInlineMarkAbsent = async (row) => {
    setRowActionId(row.employeeId);
    setRowActionError(null);
    try {
      const res = await apiClient.post('/api/attendance/mark-absent', {
        employeeId: row.employeeId,
        workDate: date,
      });
      setRows(prev => prev.map(r => (r.employeeId === res.data.employeeId ? res.data : r)));
    } catch (err) {
      setRowActionError(err.response?.data?.message || t('modal.errors.markAbsentFailed'));
    } finally {
      setRowActionId(null);
    }
  };

  const handleInlineClearAbsent = async (row) => {
    setRowActionId(row.employeeId);
    setRowActionError(null);
    try {
      const res = await apiClient.post('/api/attendance/clear-absent', {
        employeeId: row.employeeId,
        workDate: date,
      });
      setRows(prev => prev.map(r => (r.employeeId === res.data.employeeId ? res.data : r)));
    } catch (err) {
      setRowActionError(err.response?.data?.message || t('modal.errors.clearAbsentFailed'));
    } finally {
      setRowActionId(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <PageHeader title={t('title')} />

      <div className="px-6 py-6">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 pt-4">
            <div className="flex items-center gap-3">
              <label htmlFor="att-date" className="text-sm text-gray-600">{t('date')}</label>
              <input
                id="att-date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
              />
              {date !== todayYMD() && (
                <button
                  onClick={() => setDate(todayYMD())}
                  className="px-3 py-1.5 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 border border-gray-200"
                >
                  {t('today')}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {rowActionError && (
            <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {rowActionError}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ClipboardCheck size={36} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600">{t('empty')}</p>
            </div>
          )}

          {(loading || rows.length > 0) && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('columns.staff')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('columns.scheduled')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('columns.punchIn')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('columns.punchOut')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('columns.worked')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('columns.status')}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading && [...Array(3)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={7} className="px-4 py-3">
                        <div className="h-3 rounded bg-gray-100 animate-pulse" />
                      </td>
                    </tr>
                  ))}
                  {!loading && rows.map(row => (
                    <tr key={row.employeeId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.employeeName || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{formatScheduled(row.scheduledStart, row.scheduledEnd) || t('notPunched')}</td>
                      <td className="px-4 py-3 text-gray-700">{formatTime(row.punchInAt) || t('notPunched')}</td>
                      <td className="px-4 py-3 text-gray-700">{formatTime(row.punchOutAt) || t('notPunched')}</td>
                      <td className="px-4 py-3 text-gray-700">{formatWorked(row.workedMinutes) || t('notPunched')}</td>
                      <td className="px-4 py-3"><StatusPill status={row.status} t={t} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          {(row.status === 'NO_PUNCH' || row.status === 'NOT_IN_YET') && (
                            <button
                              onClick={() => handleInlineMarkAbsent(row)}
                              disabled={rowActionId === row.employeeId}
                              className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <UserX size={13} />
                              {t('markAbsentButton')}
                            </button>
                          )}
                          {row.status === 'ABSENT' && (
                            <button
                              onClick={() => handleInlineClearAbsent(row)}
                              disabled={rowActionId === row.employeeId}
                              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Undo2 size={13} />
                              {t('unmarkButton')}
                            </button>
                          )}
                          <button
                            onClick={() => setEditingRow(row)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
                          >
                            <Pencil size={13} />
                            {t('editButton')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ManualPunchModal
        open={editingRow != null}
        onClose={() => setEditingRow(null)}
        row={editingRow}
        workDate={date}
        onSaved={handleSaved}
      />
    </div>
  );
}

export default Attendance;
